import {
  Button,
  ConfirmModal,
  InputDay,
  InputSelect,
} from '@vrplatform/components';
import {
  captureSentryError,
  showApiErrorNotification,
  useApiMutation,
  useApiQuery,
  useInvalidateApiQueries,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { SelectItem } from '@vrplatform/components';
import {
  showErrorNotification,
  showSuccessNotification,
} from '@vrplatform/theme';
import { type Maybe, day, formatCurrency, utc } from '@vrplatform/utils';
import {
  Alert,
  Box,
  Group,
  InputWrapper,
  LoadingOverlay,
  Modal,
} from '@mantine/core';
import { Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { formatPercentage, whereRecurringFees } from '@vrplatform/ui-common';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FEE_TYPES } from '../_components';
import type { FeeOverviewRoute } from '../_types';
import { getRecurringFeeEnumFromFeeTypeRoute } from '../_utils';

interface ModalProps {
  opened: boolean;
  closeModal: () => void;
  listingId: Maybe<string>;
  subscription: Maybe<{
    id: string | undefined;
    startAt: string;
    endAt?: string | null | undefined;
    recurringFee: {
      id: string;
      name?: string | null | undefined;
    };
  }>;
}

export function useListingsSingleFeeTableEllipsisMenuModalsState() {
  const [opened, setOpened] = useState<{
    subscription: ModalProps['subscription'];
    listingId: NonNullable<ModalProps['listingId']>;
    modal: 'edit' | 'delete' | 'add-upcoming';
  } | null>(null);

  return {
    opened,
    setOpened,
    closeModal: () => setOpened(null),
  };
}

function useFeeQuery(recurringFeeId: Maybe<string>) {
  return useApiQuery(
    async (api, signal, args) => {
      if (!args.recurringFeeId) {
        return {
          fee: null,
        };
      }

      const fee = await api.GET('/recurring-fees/{id}', {
        params: {
          path: {
            id: args.recurringFeeId,
          },
        },
        signal,
      });

      if (fee.error) {
        throw fee.error;
      }

      const isLocked = fee.data.lock?.status === 'locked';

      return {
        fee: {
          ...fee.data,
          isLocked,
        },
      };
    },
    {
      queryGroup: 'fees',
      uniqueQueryKey: 'ListingsSingleFeeTableEllipsisMenuModals',
      variables: {
        recurringFeeId,
      },
    }
  );
}

export const ListingFeeEllipsisMenuModals = ({
  addUpcomingModal,
  deleteModal,
  subscription: initial,
  editModal,
  listingId,
  feeType,
}: {
  subscription: ModalProps['subscription'];
  listingId: ModalProps['listingId'];
  deleteModal: {
    opened: boolean;
    closeModal: () => void;
  };
  addUpcomingModal: {
    opened: boolean;
    closeModal: () => void;
  };
  editModal: {
    opened: boolean;
    closeModal: () => void;
  };
  feeType: FeeOverviewRoute;
}) => {
  const [debounced] = useDebouncedValue(initial, 500);

  const recurringFeeListingSubscription = initial || debounced;

  return (
    <>
      <Edit
        {...editModal}
        feeType={feeType}
        subscription={recurringFeeListingSubscription}
        listingId={listingId}
        type="edit"
      />
      <Edit
        {...addUpcomingModal}
        subscription={recurringFeeListingSubscription}
        feeType={feeType}
        type="add-upcoming"
        listingId={listingId}
      />
      <Delete
        {...deleteModal}
        listingId={listingId}
        subscription={recurringFeeListingSubscription}
      />
    </>
  );
};

const Delete = ({ closeModal, opened, subscription }: ModalProps) => {
  const invalidate = useInvalidateApiQueries();

  const { mutateAsync, isPending: loading } = useApiMutation(
    'delete',
    '/recurring-fees/listing-periods/{id}',
    {}
  );

  const removeFee = async () => {
    try {
      if (!subscription?.id) {
        const issue = new Error('No fee on this listing found');
        captureSentryError(issue);
        throw issue;
      }

      await mutateAsync({
        params: {
          path: {
            id: subscription.id,
          },
        },
      });

      closeModal();
      invalidate(['listings', 'fees']);
    } catch (error: any) {
      showErrorNotification({
        title: 'Failed to delete',
        message:
          error?.message ||
          "We couldn't delete this fee. Please try again later.",
      });
    }
  };

  return (
    <ConfirmModal
      type="delete"
      closeModal={closeModal}
      onSubmit={removeFee}
      loading={loading}
      opened={opened}
      title={'Are you sure you want to delete this fee from the listing?'}
      subtitle="This will remove all journal entries related to this fee."
    />
  );
};

const Edit = ({
  closeModal,
  opened,
  subscription: values,
  type,
  listingId,
  feeType,
}: ModalProps & {
  type: 'edit' | 'add-upcoming';
  feeType: FeeOverviewRoute;
}) => {
  const [{ id: teamId }] = useTeam();
  const invalidate = useInvalidateApiQueries(['fees', 'listings']);

  const currentFeeType = useMemo(() => FEE_TYPES[feeType], [feeType]);

  type FormInputs = {
    startAt: string;
    endAt: string | null | undefined;
    recurringFeeId: string | undefined;
  };

  const { data, isLoading: loadingFee } = useFeeQuery(values?.recurringFee?.id);

  const methods = useForm<FormInputs>({
    values: values
      ? {
          ...values,
          endAt: values.endAt,
          startAt: utc(values.startAt).yyyymmdd(),
          recurringFeeId:
            type === 'edit' || data?.fee?.isLocked
              ? values?.recurringFee?.id
              : undefined,
        }
      : undefined,
  });

  const startDate = methods.watch('startAt');
  const endDate = methods.watch('endAt');
  const feeId = methods.watch('recurringFeeId');

  const { mutateAsync: update } = useApiMutation(
    'put',
    '/recurring-fees/listing-periods/{id}',
    {
      onSettled: () => {
        invalidate();
      },
    }
  );

  const { mutateAsync: remove } = useApiMutation(
    'delete',
    '/recurring-fees/listing-periods/{id}',
    {
      onSettled: () => {
        invalidate();
      },
    }
  );

  const { mutateAsync: insert } = useApiMutation(
    'post',
    '/recurring-fees/listing-periods',
    {
      onSettled: () => {
        invalidate();
      },
    }
  );

  const submit = async (data: FormInputs) => {
    try {
      if (!listingId) {
        const message = 'No listing found';
        captureSentryError(new Error(message));
        throw new Error(message);
      }

      if (type === 'edit') {
        if (!values?.id) {
          const message = 'No fee on this listing found';
          captureSentryError(new Error(message));
          throw new Error(message);
        }

        const dirtyFields = methods.formState.dirtyFields;

        // If user changed recurring fee, we need to remove the existing subscription first
        if (dirtyFields.recurringFeeId) {
          await remove({
            params: {
              path: {
                id: values.id,
              },
              query: {
                onLocked: 'error',
              },
            },
          });
        }

        await update({
          params: {
            path: {
              id: values.id,
            },
          },
          body: {
            endAt: dirtyFields.endAt
              ? data.endAt
                ? day(data.endAt).add(1, 'day').yyyymmdd()
                : null
              : undefined,
            startAt: dirtyFields.startAt
              ? day(data.startAt).yyyymmdd()
              : undefined,
            onConflict: 'updateExisting',
          },
        });
      } else {
        if (!data.recurringFeeId) {
          const message = 'No recurring fee found';
          captureSentryError(new Error(message));
          throw new Error(message);
        }

        await insert({
          body: {
            listingId,
            endAt: data.endAt ? day(data.endAt).add(1, 'day').yyyymmdd() : null,
            startAt: day(data.startAt).yyyymmdd(),
            recurringFeeId: data.recurringFeeId,
            onConflict: 'updateExisting',
          },
        });
      }

      showSuccessNotification({
        title: 'Success!',
        message: 'The fee was successfully added to the listing.',
      });

      closeModal();
    } catch (error: any) {
      showApiErrorNotification({
        error,
        title: 'Failed to add fee',
        defaultMessage:
          'We failed to add the fee to the listing. Please try again later and if the problem persists, contact support.',
      });
    }
  };

  const { data: fees = [], isLoading: loadingFees } = useQuery(
    (q, args) => {
      return q
        .recurringFees({
          where: whereRecurringFees({
            tenantId: args.teamId,
            type: getRecurringFeeEnumFromFeeTypeRoute(args.feeType),
          }),
          order_by: [{ title: 'asc_nulls_last' }],
        })
        .map<SelectItem>((fee) => {
          const defaultRate = fee.defaultRate ?? 0;
          const currency = fee.tenant.defaultCurrency;
          const rateType = fee.rateType;

          return {
            value: fee.id,
            label: `${fee.title || '-'} (${rateType === 'flat' ? formatCurrency(defaultRate / 100, currency) : formatPercentage(defaultRate, 'super-base-points')})`,
            disabled: fee.id === args.currentFeeId,
          };
        });
    },
    {
      variables: {
        teamId,
        currentFeeId:
          type === 'add-upcoming' ? values?.recurringFee?.id : undefined,
        feeType,
      },
      keepPreviousData: true,
    }
  );

  const selectedFee = useMemo(() => {
    return fees.find((fee) => fee.value === feeId);
  }, [feeId, fees]);

  const singleFeeTypeLabel = useMemo(() => {
    return currentFeeType.title.slice(0, -1);
  }, [currentFeeType]);
  const editFeeName =
    values?.recurringFee?.name?.trim() || data?.fee?.name?.trim();

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={
        type === 'edit'
          ? `Update ${editFeeName || singleFeeTypeLabel.toLowerCase()}`
          : `Add upcoming ${singleFeeTypeLabel.toLowerCase()}`
      }
      onSubmit={methods.handleSubmit(submit)}
      onReset={closeModal}
      styles={(theme) => ({
        title: { fontWeight: 600 },
        content: {
          paddingInline: theme.spacing.xs,
          paddingBlock: theme.spacing.xs,
        },
      })}
      centered
    >
      <Box
        component="form"
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.lg,
          position: 'relative',
        })}
      >
        <LoadingOverlay
          visible={loadingFee}
          loaderProps={{
            size: 'sm',
          }}
        />
        {/* COMMISSION INPUT */}
        <Controller
          control={methods.control}
          name="recurringFeeId"
          rules={{
            required: `${singleFeeTypeLabel} is required`,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <InputWrapper
                label={singleFeeTypeLabel}
                error={error?.message}
                required={type === 'add-upcoming'}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
              >
                <InputSelect
                  type="single"
                  setValue={(v) => field.onChange(v?.value)}
                  value={fees.find((fee) => fee.value === field.value) ?? null}
                  data={{
                    options: fees,
                    loading: loadingFees,
                  }}
                  inputProps={{
                    error: !!error,
                    placeholder: 'Select a fee',
                    disabled: data?.fee?.isLocked || type === 'edit',
                  }}
                />
              </InputWrapper>
            );
          }}
        />

        {/* DATE INPUTS */}
        <Group
          sx={{
            '> *': {
              flex: 1,
            },
          }}
          align="flex-start"
        >
          <Controller
            control={methods.control}
            defaultValue={day().yyyymmdd()}
            name="startAt"
            rules={{
              required: 'Start date is required',
            }}
            render={({ field, fieldState: { error } }) => {
              const endAt = methods.watch('endAt');

              return (
                <InputWrapper
                  label="Start fee from"
                  error={error?.message}
                  required
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                >
                  <InputDay
                    value={field.value ? day(field.value).toDate() : null}
                    onChange={(value) =>
                      field.onChange(value ? day(value).yyyymmdd() : null)
                    }
                    placeholder={day().yyyymmdd()}
                    error={!!error}
                    maxDate={
                      endAt ? day(endAt).subtract(1, 'day').toDate() : undefined
                    }
                    clearable
                  />
                </InputWrapper>
              );
            }}
          />
          <Controller
            control={methods.control}
            name="endAt"
            render={({ field, fieldState: { error } }) => {
              const startAt = methods.watch('startAt');

              return (
                <InputWrapper
                  label={'until'}
                  error={error?.message}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                >
                  <InputDay
                    value={field.value ? day(field.value).toDate() : null}
                    onChange={(value) =>
                      field.onChange(value ? day(value).yyyymmdd() : null)
                    }
                    minDate={
                      startAt ? day(startAt).add(1, 'day').toDate() : undefined
                    }
                    clearable
                    placeholder={'Forever'}
                    error={!!error}
                    disabled={!startAt && !field.value}
                  />
                </InputWrapper>
              );
            }}
          />
        </Group>

        <Alert
          icon={
            <Icon
              icon="InfoIcon"
              size={14}
              color={(theme) => theme.colors[theme.primaryColor][5]}
              strokeWidth={1.7}
            />
          }
          styles={{
            icon: { width: 10 },
          }}
          sx={(theme) => ({
            backgroundColor: theme.colors[theme.primaryColor][0] + 80,
          })}
        >
          <Text fw={500} span>
            {selectedFee?.label ?? 'Your selected fee'}
          </Text>{' '}
          will be added to every reservation between{' '}
          <Text fw={500} span>
            {startDate ? day(startDate).format('l') : '?'}
          </Text>{' '}
          and{' '}
          {endDate ? (
            day(endDate).format('l')
          ) : (
            <Text span fw={500}>
              forever
            </Text>
          )}{' '}
          for these listings.
        </Alert>

        <Group justify="right">
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={methods.formState.isSubmitting}
            leftIcon="CheckCircleIcon"
          >
            Submit
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
